import { ComponentProps, Suspense, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { useObjectTypeSettingsLazyQuery } from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import CustomForm from "@/components/modules/CustomForm";
import { useMountEffect } from "@/hooks/useMountEffect";
import { FormType, IntegrationLogoBox } from "@/integrations";

type ObjectTypeSettingsDialogProps = {
  integrationId: string;
  connectionId: string;
  objectType: string;
  objectTypeSettings: Record<string, any> | null;
  onSave: (settings: Record<string, any>) => void;
};

export function ObjectTypeSettingsDialog(
  props: ComponentProps<typeof Modal> & ObjectTypeSettingsDialogProps,
) {
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <ModalHeader>
        <div className="flex items-center gap-3">
          <IntegrationLogoBox id={props.integrationId} />
          <p>
            Settings for <em>{props.objectType}</em>
          </p>
        </div>
      </ModalHeader>
      <ModalCloseButton />
      <Suspense fallback={<LoadingSpinner />}>
        <ObjectTypeSettingsDialogContents
          integrationId={props.integrationId}
          connectionId={props.connectionId}
          objectType={props.objectType}
          objectTypeSettings={props.objectTypeSettings}
          onSave={props.onSave}
        />
      </Suspense>
    </Modal>
  );
}

function ObjectTypeSettingsDialogContents(
  props: ObjectTypeSettingsDialogProps,
) {
  const [formDefinition, setFormDefinition] = useState<FormType | null>(null);

  const [fetchSettings, { loading }] = useObjectTypeSettingsLazyQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      objectType: props.objectType,
      connectionId: props.connectionId,
    },
    onCompleted: (data) => {
      const formDefinition = data.getObjectTypeSettings.settings;
      if (formDefinition) {
        setFormDefinition(formDefinition);
      }
    },
  });

  useMountEffect(() => {
    fetchSettings();
  });

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      settings: props.objectTypeSettings ?? {},
      __isValidHack__: true,
    },
  });

  const { isValid } = formMethods.formState;

  const settings = formMethods.watch("settings");
  return (
    <>
      <ModalBody>
        <p className="mb-4 text-sm">
          Configure the settings specific to the{" "}
          <strong>{props.objectType}</strong> object type.
        </p>
        {formDefinition === null ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <FormProvider {...formMethods}>
            <form
              id="object-type-settings-form"
              onSubmit={formMethods.handleSubmit((data) => {
                props.onSave(data.settings);
              })}
              className="flex h-full w-full flex-col"
            >
              <input
                type={"hidden"}
                {...formMethods.register("__isValidHack__", {
                  validate: (value) => {
                    return value || "Invalid";
                  },
                })}
              />
              <Controller
                name="settings"
                render={({ field }) => (
                  <CustomForm
                    form={formDefinition}
                    onChange={(settings) => {
                      field.onChange({
                        ...field.value,
                        ...settings,
                      });
                    }}
                    onChangeValid={(isValid) => {
                      if (
                        formMethods.getValues("__isValidHack__") !== isValid
                      ) {
                        formMethods.setValue("__isValidHack__", isValid, {
                          shouldValidate: true,
                        });
                      }
                    }}
                    initialValues={{
                      ...settings,
                      connectionId: props.connectionId,
                    }}
                    fetchSettings={(formValues) => {
                      fetchSettings({
                        variables: {
                          objectType: props.objectType,
                          connectionId: props.connectionId,
                          formValues,
                        },
                      });
                    }}
                  />
                )}
              />
            </form>
          </FormProvider>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          type="submit"
          form="object-type-settings-form"
          variant="solid"
          colorScheme="primary"
          isDisabled={!isValid}
          isLoading={loading}
          loadingText="Loading..."
        >
          Save Settings
        </Button>
      </ModalFooter>
    </>
  );
}
